import React, { ChangeEvent, Dispatch, SetStateAction, createContext, useMemo } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { TFunction } from 'i18next';
import { useScheduleCreate } from '../hooks/useScheduleCreate';
import { Item, PrimaryCategoryStockCount, Store2, StockCheckScheduleItem, StockCheckScheduleCountZone, CountZoneDto, ItemModel, StockCheckTypesResponse, ApproverRole, StockCheckScheduleCountZoneType } from '../../../app/services/api/generated';
import { InternalStockCheckSchedule } from '../interfaces/StockInterfaces';
import { Blocker } from 'react-router-dom';
import { ApproverType } from '../../../enums/ApproverType';

export interface ScheduleCreateValues {
  t: TFunction<'createStockCount', undefined>,
  isCategoriesModalOpen: boolean,
  isProductTypeModalOpen: boolean,
  isItemsModalOpen: boolean,
  isBulkModalOpen: boolean,
  isCategoryDeleteModalOpen: boolean,
  isProductTypeDeleteModalOpen: boolean,
  isEndOfMonthCountModalOpen: boolean,
  existingStockCheckStatus: number | null,
  existingStockCheckId: number | null,
  isDeleteModalOpen: boolean,
  allUsers: string[],
  productType: string,
  handleActiveChange: (event: SelectChangeEvent) => void,
  handleQtyChange: (itemNumber: string, newValue: string) => void,
  handleValueChange: (itemNumber: string, newValue: string) => void,
  handleLeaveClick: () => void,
  handleUserSelect: (event: SelectChangeEvent<string>) => void,
  handleSelectChange: (event: SelectChangeEvent<number>) => void,
  generateTimeOptions: () => { value: number; label: string; }[],
  handleInputValue: (event: ChangeEvent<HTMLInputElement>) => void,
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void,
  submitStock: () => void,
  handleAddBulkItemClick: () => void,
  handleAddItemsClick: () => void,
  handleAddCategoriesClick: () => void,
  handleProductTypeClickOpen: () => void,
  handleClose: () => void,
  handleitems: (data: Item[]) => void,
  deleteItemRow: (itemNumberToDelete?: string) => void,
  deleteCategoryRow: (nameToDelete?: string) => void,
  deleteProductType: () => void,
  deleteProductTypeRow: (productType: string) => void,
  deleteItems: () => void,
  deleteCategories: () => void,
  deleteProductTypes: (data: string[]) => void,
  handleCategories: (data: PrimaryCategoryStockCount[]) => void,
  handleProductTypes: (data: string[]) => void,
  handleSliderChange: (event: Event, newValue: number | number[]) => void,
  handleIsTimeLockedChanged: (event: SelectChangeEvent) => void,
  setIsEndOfMonthCountModalOpen: Dispatch<SetStateAction<boolean>>,
  name: string,
  today: string,
  selectedUser: string,
  approverType: ApproverType,
  approverRoles: ApproverRole[],
  selectedApproverRoleId: number | undefined,
  handleApproverTypeChange: (event: SelectChangeEvent<number>) => void,
  handleApproverRoleSelect: (event: SelectChangeEvent<number>) => void,
  approvalValue: number | undefined,
  selectedTime: number | undefined,
  isTimeLocked: boolean,
  sliderValue: number,
  categoryData: PrimaryCategoryStockCount[],
  productTypeData: string[],
  itemsData: StockCheckScheduleItem[],
  countZonesData: StockCheckScheduleCountZone[],
  setCountZonesData: Dispatch<SetStateAction<StockCheckScheduleCountZone[]>>,
  closeSnackBar: () => void,
  isSnackBarOpen: boolean,
  snackBarSeverity: 'error' | 'warning' | 'info' | 'success',
  snackbarMessage: string,
  snackBarDuration: number | null,
  schedules: InternalStockCheckSchedule[],
  stores: Store2[],
  setSchedules: Dispatch<SetStateAction<InternalStockCheckSchedule[]>>,
  deleteScheduleRow: (index?: number) => void,
  isScheduleDeleteModalOpen: boolean,
  confirmDeleteSchedule: () => void,
  handleSmartCountChange: () => void,
  isSmartCount: boolean,
  handleCategoryChange: (index: number, newValue: string) => void,
  isActive: boolean,
  isLoading: boolean,
  generateStock: () => void,
  isCreating: boolean,
  blocker: Blocker,
  handleLeavePageModalClose: () => void,
  handleLeavePageModalConfirm: () => void,
  currencySymbol: string,
  hasRightToEdit: boolean,
  isCountZoneConfigEnabled: boolean,
  handleAddCountZonesClick: () => void,
  isCountZonesModalOpen: boolean,
  handleCountZones: (data: CountZoneDto[]) => void,
  deleteCountZoneRow: (idToDelete?: number) => void,
  isCountZonesDeleteModalOpen: boolean,
  deleteCountZones: () => void,
  handleViewCountZoneItemsClick: (countZoneId: number) => void,
  isCountZonesWithItemsModalOpen: boolean,
  countZoneItemsData: ItemModel[],
  isCountZoneTypesModalOpen: boolean,
  isCountZoneTypesDeleteModalOpen: boolean,
  countZoneTypesData: StockCheckScheduleCountZoneType[],
  setCountZoneTypesData: Dispatch<SetStateAction<StockCheckScheduleCountZoneType[]>>,
  countZoneTypeId: number | undefined,
  handleAddCountZoneTypesClick: () => void,
  handleCountZoneTypes: (data: StockCheckScheduleCountZoneType[]) => void,
  deleteCountZoneTypeRow: (idToDelete?: number) => void,
  deleteCountZoneTypes: () => void,
  handleStockCheckTypeChange: (event: SelectChangeEvent<number>) => void,
  stockCheckTypes: StockCheckTypesResponse[],
  stockCheckTypeId: number | undefined,
  strictEmailApprovalEnabled: boolean | null,
  autoApprovalEnabled: boolean,
  isToleranceSliderToggled: boolean,
  handleIsToleranceSliderToggledChange: (event: SelectChangeEvent) => void
}

export const ScheduleCreateContext = createContext<null | ScheduleCreateValues>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export const ScheduleCreateProvider = ({ children }: ProviderProps) => {
  const {
    t,
    isCategoriesModalOpen,
    isProductTypeModalOpen,
    isItemsModalOpen,
    isBulkModalOpen,
    isCategoryDeleteModalOpen,
    isProductTypeDeleteModalOpen,
    isEndOfMonthCountModalOpen,
    existingStockCheckStatus,
    existingStockCheckId,
    isDeleteModalOpen,
    allUsers,
    productType,
    handleActiveChange,
    handleQtyChange,
    handleValueChange,
    handleLeaveClick,
    handleUserSelect,
    handleSelectChange,
    generateTimeOptions,
    handleInputValue,
    handleInputChange,
    submitStock,
    handleAddBulkItemClick,
    handleAddItemsClick,
    handleAddCategoriesClick,
    handleProductTypeClickOpen,
    handleClose,
    handleitems,
    deleteItemRow,
    deleteCategoryRow,
    deleteProductType,
    deleteProductTypeRow,
    deleteItems,
    deleteCategories,
    deleteProductTypes,
    handleCategories,
    handleProductTypes,
    handleSliderChange,
    handleIsTimeLockedChanged,
    setIsEndOfMonthCountModalOpen,
    name,
    today,
    selectedUser,
    approverType,
    approverRoles,
    selectedApproverRoleId,
    handleApproverTypeChange,
    handleApproverRoleSelect,
    approvalValue,
    selectedTime,
    isTimeLocked,
    sliderValue,
    categoryData,
    productTypeData,
    itemsData,
    countZonesData,
    setCountZonesData,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    schedules,
    stores,
    setSchedules,
    deleteScheduleRow,
    isScheduleDeleteModalOpen,
    confirmDeleteSchedule,
    handleSmartCountChange,
    isSmartCount,
    handleCategoryChange,
    isActive,
    isLoading,
    generateStock,
    isCreating,
    blocker,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    currencySymbol,
    hasRightToEdit,
    isCountZoneConfigEnabled,
    handleAddCountZonesClick,
    isCountZonesModalOpen,
    handleCountZones,
    deleteCountZoneRow,
    isCountZonesDeleteModalOpen,
    deleteCountZones,
    handleViewCountZoneItemsClick,
    isCountZonesWithItemsModalOpen,
    countZoneItemsData,
    isCountZoneTypesModalOpen,
    isCountZoneTypesDeleteModalOpen,
    countZoneTypesData,
    setCountZoneTypesData,
    countZoneTypeId,
    handleAddCountZoneTypesClick,
    handleCountZoneTypes,
    deleteCountZoneTypeRow,
    deleteCountZoneTypes,
    handleStockCheckTypeChange,
    stockCheckTypes,
    stockCheckTypeId,
    strictEmailApprovalEnabled,
    autoApprovalEnabled,
    isToleranceSliderToggled,
    handleIsToleranceSliderToggledChange,
  } = useScheduleCreate();

  const passedValue = {
    t,
    isCategoriesModalOpen,
    isProductTypeModalOpen,
    isItemsModalOpen,
    isBulkModalOpen,
    isCategoryDeleteModalOpen,
    isProductTypeDeleteModalOpen,
    isEndOfMonthCountModalOpen,
    existingStockCheckStatus,
    existingStockCheckId,
    isDeleteModalOpen,
    allUsers,
    productType,
    handleActiveChange,
    handleQtyChange,
    handleValueChange,
    handleLeaveClick,
    handleUserSelect,
    handleSelectChange,
    generateTimeOptions,
    handleInputValue,
    handleInputChange,
    submitStock,
    handleAddBulkItemClick,
    handleAddItemsClick,
    handleAddCategoriesClick,
    handleProductTypeClickOpen,
    handleClose,
    handleitems,
    deleteItemRow,
    deleteCategoryRow,
    deleteProductType,
    deleteProductTypeRow,
    deleteItems,
    deleteCategories,
    deleteProductTypes,
    handleCategories,
    handleProductTypes,
    handleSliderChange,
    handleIsTimeLockedChanged,
    setIsEndOfMonthCountModalOpen,
    name,
    today,
    selectedUser,
    approverType,
    approverRoles,
    selectedApproverRoleId,
    handleApproverTypeChange,
    handleApproverRoleSelect,
    approvalValue,
    selectedTime,
    isTimeLocked,
    sliderValue,
    categoryData,
    productTypeData,
    itemsData,
    countZonesData,
    setCountZonesData,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    schedules,
    stores,
    setSchedules,
    deleteScheduleRow,
    isScheduleDeleteModalOpen,
    confirmDeleteSchedule,
    handleSmartCountChange,
    isSmartCount,
    handleCategoryChange,
    isActive,
    isLoading,
    generateStock,
    isCreating,
    blocker,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    currencySymbol,
    hasRightToEdit,
    isCountZoneConfigEnabled,
    handleAddCountZonesClick,
    isCountZonesModalOpen,
    handleCountZones,
    deleteCountZoneRow,
    isCountZonesDeleteModalOpen,
    deleteCountZones,
    handleViewCountZoneItemsClick,
    isCountZonesWithItemsModalOpen,
    countZoneItemsData,
    isCountZoneTypesModalOpen,
    isCountZoneTypesDeleteModalOpen,
    countZoneTypesData,
    setCountZoneTypesData,
    countZoneTypeId,
    handleAddCountZoneTypesClick,
    handleCountZoneTypes,
    deleteCountZoneTypeRow,
    deleteCountZoneTypes,
    handleStockCheckTypeChange,
    stockCheckTypes,
    stockCheckTypeId,
    strictEmailApprovalEnabled,
    autoApprovalEnabled,
    isToleranceSliderToggled,
    handleIsToleranceSliderToggledChange,
  };

  const fireBaseProviderValue = useMemo(() => ({ passedValue }), [passedValue]);

  return (
    <ScheduleCreateContext.Provider value={fireBaseProviderValue.passedValue}>
      {children}
    </ScheduleCreateContext.Provider>
  );
};